import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { APPROXIMATE_CARD_HEIGHT, INITIAL_KANBAN_COLUMN_ELEMENTS } from "../../config";
import { getKanbanStatusOptions } from "../../constants";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { ApplicationStateEnum, AssetUploadResponse } from "../../network/APITypes";
import { authStore } from "../../stores/AuthStore";
import { generalStore } from "../../stores/GeneralStore";
import { EMPTY_DIALOG_FORM, hrStore } from "../../stores/HrStore";
import { TableStore } from "../../stores/TableStore";
import { IApplicationStateEnum, JobCategoryEnum } from "../../types/models";
import { useApplicants } from "../hooks/useApplicants";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { NoResults } from "../ui/EmptyStates";
import { Column, KanbanColumn } from "../ui/KanbanColumn";
import { formatISODateOnly, templateToString, transitionToTemplate } from "../util/helpers";
import { HrKanbanDialog } from "./dialogs/HrKanbanDialog";
import { THEME } from "../../theme/ThemeStore";

const onDragEnd = (
    result: DropResult,
    columns: Column[],
    setOpenDialog: React.Dispatch<React.SetStateAction<ApplicationStateEnum | undefined>>,
    setId: React.Dispatch<React.SetStateAction<string | null>>,
    setApplicantFirstName: React.Dispatch<React.SetStateAction<string>>
) => {
    const { source, destination } = result;
    const isDal = THEME.THEME_NAME === "dal";

    const sourceIndex = Number(source.droppableId);
    const destinationIndex = Number(destination?.droppableId);

    if (!destination || destinationIndex < sourceIndex) {
        return;
    }
    if (source.droppableId !== destination.droppableId) {
        const sourceColumn: Column = columns[sourceIndex];
        const destColumn: Column = columns[destinationIndex];
        const sourceItems = sourceColumn.items ? [...sourceColumn.items] : [];
        const [removed] = sourceItems.splice(source.index, 1);

        setId(removed.id);
        setApplicantFirstName(removed.firstName);

        if (destColumn.status !== "application_received") {
            const template = transitionToTemplate(
                sourceColumn.status,
                destColumn.status,
                removed.jobCategory as JobCategoryEnum
            );

            const title = () => {
                if (destColumn.status === "first_interview") {
                    return t("screen.dashboard.dialog.firstInterview.title", {
                        name: `${removed.firstName} ${removed.lastName}`,
                        company: t(isDal ? "company.dal" : "company.aaa")
                    });
                } else if (destColumn.status === "second_interview") {
                    return t("screen.dashboard.dialog.secondInterview.title", {
                        name: `${removed.firstName} ${removed.lastName}`,
                        company: t(isDal ? "company.dal" : "company.aaa")
                    });
                }
            };

            hrStore.currentDialogForm = {
                ...EMPTY_DIALOG_FORM,
                template: template,
                language: removed.language,
                message: templateToString(removed.language, template, {
                    firstName: removed.firstName,
                    userName: authStore.givenName ?? ""
                }),
                title: title()
            };

            setOpenDialog(destColumn.status);
        }
    }
};

type KanbanBoardProps = { jobCategories: Array<JobCategoryEnum>; tableStore: TableStore };

export const KanbanBoard = observer(({ jobCategories, tableStore }: KanbanBoardProps) => {
    const [applicantId, setApplicantId] = React.useState<string | null>(null);
    const [applicantFirstName, setApplicantFirstName] = React.useState("");

    const [columnsOrderDir, switchColumnsOrderDir] = React.useState<("asc" | "desc")[]>(
        getKanbanStatusOptions().map(() => "desc")
    );

    const isMobile = useMobileMediaQuery();
    const kanbanStatusOptions = getKanbanStatusOptions();

    // variable used to the determine the type of HrKanbanDialog
    const [destinationApplicationStatus, setDestinationApplicationStatus] = React.useState<
        ApplicationStateEnum | undefined
    >(undefined);

    // save the source of the currently dragged item (used to determine where it can be dropped as well as which column to reload, as well as where to show placeholder)
    const [sourceColumnIndex, setSourceColumnIndex] = React.useState<number | undefined>(undefined);
    const [sourceItemIndex, setSourceItemIndex] = React.useState<number | undefined>(undefined);

    const kanbanBoardRef = React.useRef<HTMLDivElement | null>(null);

    const columns = [
        {
            label: t(kanbanStatusOptions[0].messageId),
            status: kanbanStatusOptions[0].value,
            items: useApplicants(
                {
                    jobCategory: jobCategories,
                    applicationState: kanbanStatusOptions[0].value,
                    keyword: tableStore.keyword,
                    orderBy: "createdAt",
                    orderDir: columnsOrderDir[0]
                },
                undefined,
                hrStore.forceReloadApplicationState
            )
        },
        ...kanbanStatusOptions.slice(1, kanbanStatusOptions.length - 1).map((option, columnIndex) => ({
            label: t(option.messageId),
            status: option.value,
            items: useApplicants(
                {
                    jobCategory: jobCategories,
                    applicationState: option.value,
                    keyword: tableStore.keyword,
                    orderBy: "createdAt",
                    orderDir: columnsOrderDir[columnIndex + 1]
                },
                undefined,
                hrStore.forceReloadApplicationState
            )
        })),
        {
            label: t(kanbanStatusOptions[kanbanStatusOptions.length - 1].messageId),
            status: kanbanStatusOptions[kanbanStatusOptions.length - 1].value,
            items: useApplicants(
                {
                    jobCategory: jobCategories,
                    applicationState: kanbanStatusOptions[kanbanStatusOptions.length - 1].value,
                    keyword: tableStore.keyword,
                    orderBy: "createdAt",
                    orderDir: columnsOrderDir[kanbanStatusOptions.length - 1],
                    lastChangeFrom: formatISODateOnly(moment().subtract(1, "weeks"))
                },
                undefined,
                hrStore.forceReloadApplicationState
            )
        }
    ];

    // has to be type IApplicationStateEnum instead of ApplicationStateEnum because HrKanbanColumn can be opened with type evidence from Employee Details View
    // thus the HrKanbanColumn submit function is type IApplicationStateEnum
    const handleSubmitDialog = async (type?: IApplicationStateEnum) => {
        try {
            generalStore.isLoading = true;
            if (applicantId && type) {
                if (type === "hired" && hrStore.currentDialogForm.contract) {
                    const assetResponse: AssetUploadResponse = await API.postAsset(
                        hrStore.currentDialogForm.contract,
                        generalStore.locale
                    );
                    await API.postTransition(applicantId, hrStore.currentDialogForm, type, assetResponse.id);
                    // handle type difference IApplicationStateEnum / ApplicationStateEnum
                } else if (type !== "evidence") {
                    await API.postTransition(applicantId, hrStore.currentDialogForm, type);
                }
            }

            // set helper variable to reload affected columns
            if (sourceColumnIndex !== undefined) {
                // Reload source column
                await columns[sourceColumnIndex].items.reload();
                // Reload destination column
                await columns[columns.findIndex((column) => column.status === type)]?.items.reload();
            }

            // reset HrKanbanColumn information
            setDestinationApplicationStatus(undefined);
            hrStore.currentDialogForm = EMPTY_DIALOG_FORM;
            setApplicantFirstName("");
        } catch (err) {
            generalStore.setError(t("error.transferApplicant"), err);
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleCloseDialog = () => {
        // reset HrKanbanColumn information
        setDestinationApplicationStatus(undefined);
        hrStore.currentDialogForm = EMPTY_DIALOG_FORM;
        setApplicantFirstName("");
    };

    const displayEmptyState =
        columns.every((column) => column.items.applicants.length === 0 && column.items.initialLoaded) &&
        !generalStore.isLoading;

    return (
        <>
            {!displayEmptyState && (
                <div
                    ref={kanbanBoardRef}
                    style={{
                        display: "flex",
                        flexGrow: 1,
                        flexDirection: isMobile ? "column" : "row"
                    }}
                >
                    <DragDropContext
                        onDragEnd={(result: DropResult) =>
                            onDragEnd(
                                result,
                                columns.map((column) => ({
                                    label: column.label,
                                    status: column.status,
                                    items: column.items.applicants,
                                    total: column.items.total,
                                    unreadApplicationsTotal: column.items.unreadApplicationsTotal
                                })),
                                setDestinationApplicationStatus,
                                setApplicantId,
                                setApplicantFirstName
                            )
                        }
                    >
                        {columns.map((column, index) => (
                            <KanbanColumn
                                column={{
                                    label: column.label,
                                    status: column.status,
                                    items: column.items.applicants,
                                    total: column.items.total,
                                    unreadApplicationsTotal: column.items.unreadApplicationsTotal
                                }}
                                columnIndex={index}
                                key={column.status}
                                itemSourceColumnIndex={sourceColumnIndex}
                                itemSourceItemIndex={sourceItemIndex}
                                columnsOrderDir={columnsOrderDir}
                                setColumnsOrderDir={switchColumnsOrderDir}
                                switchColumnsChangeToggle={column.items.reload}
                                setSourceItemIndex={setSourceItemIndex}
                                setSourceColumnIndex={setSourceColumnIndex}
                                onLoadMore={column.items.loadMore}
                            />
                        ))}
                    </DragDropContext>
                </div>
            )}

            {displayEmptyState && <NoResults />}
            <HrKanbanDialog
                onSubmit={handleSubmitDialog}
                onBack={handleCloseDialog}
                type={destinationApplicationStatus}
                firstName={applicantFirstName}
                fromApplicationState={sourceColumnIndex ? columns[sourceColumnIndex].status : undefined}
                jobCategory={
                    columns[sourceColumnIndex ?? 0].items.applicants.find((applicant) => applicant.id === applicantId)
                        ?.jobCategory
                }
            />
        </>
    );
});
